import { z } from 'zod'

// ==================== 订单相关 ====================

// 订单状态枚举
export const ORDER_STATUS = {
  DELETED: -3,
  CANCELLED: -2,
  PENDING_PAYMENT: -1,
  PENDING_SHIPMENT: 0,
  SHIPPED: 1,
  COMPLETED: 2,
  REFUNDED: 3,
} as const

export const ORDER_STATUS_LABELS: Record<number, string> = {
  [-3]: '已删除',
  [-2]: '已取消',
  [-1]: '待支付',
  [0]: '待发货',
  [1]: '已发货',
  [2]: '已完成',
  [3]: '已退款',
}

// 订单状态筛选选项：按业务流程排序（待支付 → 待发货 → 已发货 → 已完成 → 已退款 → 已取消，排除已删除）
export const ORDER_STATUS_FLOW: number[] = [-1, 0, 1, 2, 3, -2]

// 支付方式
export const PAY_TYPE = {
  WECHAT: 1,
  BALANCE: 2,
  OFFLINE: 3,
  ALIPAY: 4,
  POINTS: 5,
} as const

export const PAY_TYPE_LABELS: Record<number, string> = {
  [PAY_TYPE.WECHAT]: '微信支付',
  [PAY_TYPE.BALANCE]: '余额支付',
  [PAY_TYPE.OFFLINE]: '线下支付',
  [PAY_TYPE.ALIPAY]: '支付宝',
  [PAY_TYPE.POINTS]: '积分支付',
}

// 订单列表查询参数
export const orderListParamsSchema = z.object({
  page: z.number().default(1),
  page_size: z.number().default(30),
  status: z.string().optional(),
  real_name: z.string().optional(),
  data: z.string().optional(),
  pay_type: z.string().optional(),
  type: z.string().optional(),
})

export type OrderListParams = z.infer<typeof orderListParamsSchema>

// 订单商品信息
export const orderCartInfoSchema = z.object({
  cart_id: z.string(), // 雪花ID
  product_id: z.number(),
  product_name: z.string(),
  product_image: z.string(),
  product_attr: z.string().optional(),
  cart_num: z.number(),
  price: z.number(),
  cost_price: z.number().optional(),
  total_price: z.number(),
  pay_price: z.number().optional(), // 实付小计（促销组合分摊后），无促销时与 total_price 一致
  sku: z.string().optional(),
})

export type OrderCartInfo = z.infer<typeof orderCartInfoSchema>

// 订单实体
export const orderSchema = z.object({
  id: z.number(),
  order_id: z.string(),
  uid: z.number(),
  real_name: z.string(),
  user_phone: z.string(),
  user_address: z.string(),
  total_num: z.number(),
  total_price: z.number(),
  pay_price: z.number(),
  status: z.number(),
  refund_type: z.number().optional(),
  refund_price: z.number().optional(),
  add_time: z.string(),
  paid: z.number(),
  pay_type: z.string().optional(),
  paid_time: z.string().optional(),
  supplier_id: z.number(),
  cart_info: z.array(orderCartInfoSchema).optional(),
  delivery_name: z.string().optional(),
  delivery_id: z.string().optional(),
  delivery_code: z.string().optional(),
  delivery_time: z.string().optional(),
  mark: z.string().optional(),
  remark: z.string().optional(),
  is_store: z.boolean().optional(),
  is_cash: z.boolean().optional(),
  is_new: z.number().optional(),
  shipping_partial: z.boolean().optional(),
  shipping_remark: z.string().optional(),
})

export type Order = z.infer<typeof orderSchema>

// 订单详情响应（后端返回的结构）
export const orderDetailResponseSchema = z.object({
  order: orderSchema,
  cart_infos: z.array(orderCartInfoSchema).optional(),
})

export type OrderDetailResponse = z.infer<typeof orderDetailResponseSchema>

// 发货请求参数
export const deliveryParamsSchema = z.object({
  type: z.enum(['1', '2', '3']), // 1=快递 2=送货 3=虚拟
  delivery_name: z.string().optional(),
  delivery_id: z.string().optional(),
  delivery_code: z.string().optional(),
  express_record_type: z.number().optional(),
  express_temp_id: z.string().optional(),
  to_name: z.string().optional(),
  to_tel: z.string().optional(),
  to_addr: z.string().optional(),
  sh_delivery_name: z.string().optional(),
  sh_delivery_id: z.string().optional(),
  sh_delivery_uid: z.string().optional(),
  delivery_type: z.number().optional(),
  station_type: z.number().optional(),
  cargo_weight: z.number().optional(),
  mark: z.string().optional(),
  remark: z.string().optional(),
  fictitious_content: z.string().optional(),
})

export type DeliveryParams = z.infer<typeof deliveryParamsSchema>

// 拆单发货请求参数
export const splitDeliveryParamsSchema = deliveryParamsSchema.extend({
  cart_ids: z.array(z.object({
    cart_id: z.string(), // 雪花ID
    cart_num: z.number(),
  })),
})

export type SplitDeliveryParams = z.infer<typeof splitDeliveryParamsSchema>

// ==================== 退款相关 ====================

// 申请类型枚举（apply_type）
export const APPLY_TYPE = {
  REFUND_ONLY: 1,      // 仅退款
  RETURN_REFUND: 2,    // 退货退款
  STORE_RETURN: 3,     // 到店退货
  PLATFORM: 4,         // 平台退款
} as const

export const APPLY_TYPE_LABELS: Record<number, string> = {
  [APPLY_TYPE.REFUND_ONLY]: '仅退款',
  [APPLY_TYPE.RETURN_REFUND]: '退货退款',
  [APPLY_TYPE.STORE_RETURN]: '到店退货',
  [APPLY_TYPE.PLATFORM]: '平台退款',
}

// 退款状态枚举（refund_type）
export const REFUND_STATUS = {
  PENDING: 0,          // 待处理
  REJECTED: 3,         // 已拒绝
  AGREE_RETURN: 4,     // 同意退货
  RETURNED: 5,         // 已退货
  REFUNDED: 6,         // 已退款
} as const

export const REFUND_STATUS_LABELS: Record<number, string> = {
  [REFUND_STATUS.PENDING]: '待处理',
  [REFUND_STATUS.REJECTED]: '已拒绝',
  [REFUND_STATUS.AGREE_RETURN]: '同意退货',
  [REFUND_STATUS.RETURNED]: '已退货',
  [REFUND_STATUS.REFUNDED]: '已退款',
}

// 兼容旧代码的枚举（deprecated，请使用 APPLY_TYPE 和 REFUND_STATUS）
export const REFUND_TYPE = {
  NONE: 0,
  REFUND_ONLY: 1,
  RETURN_REFUND: 2,
  REJECTED: 3,
  PENDING_RETURN: 4,
  RETURNING: 5,
  REFUNDED: 6,
} as const

export const REFUND_TYPE_LABELS: Record<number, string> = {
  [REFUND_TYPE.NONE]: '未申请',
  [REFUND_TYPE.REFUND_ONLY]: '仅退款',
  [REFUND_TYPE.RETURN_REFUND]: '退货退款',
  [REFUND_TYPE.REJECTED]: '已拒绝',
  [REFUND_TYPE.PENDING_RETURN]: '待退货',
  [REFUND_TYPE.RETURNING]: '退货中',
  [REFUND_TYPE.REFUNDED]: '已退款',
}

// 退款列表查询参数
export const refundListParamsSchema = z.object({
  page: z.number().default(1),
  page_size: z.number().default(30),
  order_id: z.string().optional(),
  time: z.string().optional(),
  apply_type: z.number().optional(),
  refund_type: z.number().optional(),
  refund_reason: z.string().optional(),
})

export type RefundListParams = z.infer<typeof refundListParamsSchema>

// 退款实体
export const refundSchema = z.object({
  id: z.number(),
  order_id: z.string(),
  store_order_id: z.number(),
  uid: z.number(),
  refund_type: z.number(),
  refund_price: z.number(),
  apply_type: z.number(),
  refund_reason: z.string().optional(),
  refund_explain: z.string().optional(),
  refund_img: z.array(z.string()).optional(),
  add_time: z.string(),
  refund_time: z.string().optional(),
  refuse_reason: z.string().optional(),
  delivery_name: z.string().optional(),
  delivery_id: z.string().optional(),
  cart_info: z.array(orderCartInfoSchema).optional(),
  order_info: orderSchema.optional(),
})

export type Refund = z.infer<typeof refundSchema>

// 退款处理请求参数
export const refundProcessParamsSchema = z.object({
  refund_price: z.number().min(0.01, '退款金额必须大于0'),
  type: z.number().int().min(1).max(2), // 1=同意 2=拒绝
  refuse_reason: z.string().optional(),
}).refine(
  (data) => {
    if (data.type === 2 && !data.refuse_reason) {
      return false
    }
    return true
  },
  {
    message: '拒绝退款时必须填写拒绝原因',
    path: ['refuse_reason'],
  }
)

export type RefundProcessParams = z.infer<typeof refundProcessParamsSchema>

// 退款原因
export const refundReasonSchema = z.object({
  id: z.number(),
  name: z.string(),
})

export type RefundReason = z.infer<typeof refundReasonSchema>

// ==================== 订单状态记录 ====================

// 订单状态记录实体
export const orderStatusSchema = z.object({
  id: z.number(),
  oid: z.number(),
  change_type: z.string(),
  change_message: z.string(),
  change_time: z.string(),
  change_manager_type: z.string(),
  change_manager_id: z.number(),
  change_manager: z.string(),
})

export type OrderStatus = z.infer<typeof orderStatusSchema>

// 订单状态类型标签
export const ORDER_CHANGE_TYPE_LABELS: Record<string, string> = {
  'create': '创建订单',
  'pay': '支付成功',
  'delivery': '已发货',
  'receive': '已收货',
  'complete': '订单完成',
  'cancel': '取消订单',
  'refund': '申请退款',
  'refund_success': '退款成功',
  'remark': '订单备注',
  'edit': '修改订单',
}
